import React, { SyntheticEvent, useEffect, useState } from 'react';
import {
  H3,
  Caption,
  LinkText,
  ModalHeading,
  Paragraph1,
  LegalDiv,
  ModalCloseIcon,
} from '@styles/Global.styles';
import {
  DetailContainer,
  DetailHeading,
  PlateDesignPricing,
  PricingFrom,
  PricingAmount,
  Description,
  ColorPaletteContainer,
  PlateDesignSizes,
  ErrorHeading,
  DealerListItem,
  DealerContact,
  DealerPhone,
  CaptionPositionWrapper,
  PlateSizeDropdownContainer,
  PriceLoadingMessage,
  DealerLink,
  PlateHoleGuideLink,
} from './PlateDesignDetails.styles';
import {
  PlateColorPalette,
  TextColorPalette,
} from '@components/PlateColorPalette';
import { Grid, Modal, List, DropdownProps, Icon } from 'semantic-ui-react';
import { PlateDesignDetailsProps } from './Types';
import {
  CaptionPosition,
  PlateDesignModel,
  TextColor,
  TransactionTypesSetting,
} from '~/dataModels/ProductModels';
import { GA4Event, MessagePlateLocations, OrderTypes } from '@utils/Constant';
import { PlateDropdownOptions } from './PlateDropdown';
import { MessagePlateSection } from './MessagePlateSection';
import {
  convertPlateSizeTopDropdownOptions,
  convertCaptionPositionsToDropdownOptions,
  convertBrandsTopDropdownOptions,
} from './mapper';
import theme from '@styles/theme';
import { AuthorizedDealers } from '~/dataModels/ProductModels/AuthorizedDealers';
import { isDefaultCombination, isMobile } from '@utils/Helpers';
import { PlateService } from '@services/PlateService';
//import { PlateSizeGuide } from "@components/PlateSizeGuide/index"
import { GAService } from '@services/index';
import { AfterpayMessage } from '@utils/Afterpay';
import {
  mapCartItemToGAEcommerceEventModel,
  mapViewItemToGAEcommerceEventModel,
} from '@mappers/GoogleAnalytics/GoogleAnalytics.mapper';
import { LockedMessagePlateSection } from './LockedMessagePlateSection';
import { CombinationPaymentList } from '@components/CombinationPricingOptions/CombinationPaymentList';
import { KpButton } from '../elements';
import { PlateSizeGuide } from '../PlateSizeGuide';
import { useFetcher, useNavigate } from '@remix-run/react';
import { BasketLineRequestModel } from '~/dataModels/BasketModel/BasketLineRequestModel';
import { useCartStore } from '~/stores/cartStore';
import { useCheckoutUIStore } from '~/stores/checkoutUIStore';
import { useAuthContext } from '~/contexts/AuthContext';
import { useClientEnv } from '~/contexts/ClientEnvContext';
import {
  CheckoutData,
  defaultCheckoutData,
} from '~/dataModels/Checkout/Checkout';
import { SessionService } from '~/services/SessionService';

const getCurrentCaptionPosition = (
  frontCaption: CaptionPosition | undefined,
  backCaption: CaptionPosition | undefined,
): CaptionPosition | undefined => {
  if (frontCaption !== undefined) {
    return frontCaption;
  } else if (backCaption !== undefined) {
    return backCaption;
  } else {
    return undefined;
  }
};

interface PlateDesignDetailsStates {
  topMessageMissing: boolean;
  bottomMessageMissing: boolean;
  isAddToCartClick: boolean;
  isBuyAsGiftClick: boolean;
  isHover: boolean;
}

const PlateDesignDetails: React.FunctionComponent<PlateDesignDetailsProps> = (
  props,
) => {
  //static defaultProps: { darkTheme: boolean }
  const navigate = useNavigate();
  const clientEnv = useClientEnv();
  const { basket, addBasketLine, deleteBasketLine } = useCartStore();
  const { ownerIsApplicant, giftingType, updateMultiple, updateState } =
    useCheckoutUIStore();
  const { isUserLoggedIn, user, selectedDealerId } =
    useAuthContext();
  const fetcher = useFetcher();
  const [state, setState] = useState<PlateDesignDetailsStates>({
    topMessageMissing: false,
    bottomMessageMissing: false,
    isBuyAsGiftClick: false,
    isAddToCartClick: false,
    isHover: false,
  });
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  useEffect(() => {
    triggerChooseDesignFloodlight();
  }, []);

  useEffect(() => {
    triggerChooseDesignFloodlight();
  }, [props.currentPlateDesign.frontPlate?.plateDesignCode]);

  const getTopCatpion = (frontPlate: PlateDesignModel | undefined) => {
    if (frontPlate == undefined) return undefined;
    if (
      frontPlate.captionPositon &&
      (frontPlate.captionPositon.value === MessagePlateLocations.TopAndBottom ||
        frontPlate.captionPositon.value === MessagePlateLocations.TopOnly)
    ) {
      const topMessageColor = frontPlate.topMessageColor
        ? frontPlate.topMessageColor
        : undefined;
      const topMessageText = frontPlate.topMessageText
        ? frontPlate.topMessageText
        : undefined;
      if (topMessageColor && topMessageText) {
        return {
          captionText: topMessageText,
          captionColourId: topMessageColor.id,
        };
      } else {
        return undefined;
      }
    } else {
      return undefined;
    }
  };
  const getBottomCatpion = (frontPlate: PlateDesignModel | undefined) => {
    if (frontPlate == undefined) return undefined;
    if (
      frontPlate.captionPositon &&
      (frontPlate.captionPositon.value === MessagePlateLocations.TopAndBottom ||
        frontPlate.captionPositon.value === MessagePlateLocations.BottomOnly)
    ) {
      const bottomMessageColor = frontPlate.bottomMessageColor
        ? frontPlate.bottomMessageColor
        : undefined;
      const bottomMessageText = frontPlate.bottomMessageText
        ? frontPlate.bottomMessageText
        : undefined;
      if (bottomMessageColor && bottomMessageText) {
        return {
          captionText: bottomMessageText,
          captionColourId: bottomMessageColor.id,
        };
      } else {
        return undefined;
      }
    } else {
      return undefined;
    }
  };
  const getPlateDetails = (
    frontPlate: PlateDesignModel | undefined,
    backPlate: PlateDesignModel | undefined,
  ) => {
    if (frontPlate == undefined)
      throw new Error('Failed to load plate details');
    if (
      backPlate === undefined ||
      frontPlate.plateDesignCode === backPlate.plateDesignCode
    ) {
      return {
        plateDesignId: frontPlate.plateDesignCode,
        textColorId: frontPlate.textColor.id,
        plateSizeId: frontPlate.plateSize.id,
        dualPlateDesignId: undefined,
        dualPlateTextColorId: undefined,
        dualPlateSizeId: undefined,
      };
    } else {
      return {
        plateDesignId: frontPlate.plateDesignCode,
        textColorId: frontPlate.textColor.id,
        plateSizeId: frontPlate.plateSize.id,
        dualPlateDesignId: backPlate.plateDesignCode,
        dualPlateTextColorId: backPlate.textColor.id,
        dualPlateSizeId: backPlate.plateSize.id,
      };
    }
  };
  const handleAddToCart = (buyAsGift: boolean) => {
    setIsAddingToCart(true);
    if (validateForm()) {
      //continue to submit form
      const topCaption = getTopCatpion(props.currentPlateDesign.frontPlate);
      const bottomCaption = getBottomCatpion(
        props.currentPlateDesign.frontPlate,
      );
      const plates = getPlateDetails(
        props.currentPlateDesign.frontPlate,
        props.currentPlateDesign.backPlate,
      );

      let formattedCombinationNoTrailingWhitespace = props.formattedCombination;

      // remove trailing whitespace if it's only one whitespace at the end
      if (
        formattedCombinationNoTrailingWhitespace
          .toString()
          .charAt(formattedCombinationNoTrailingWhitespace.length - 1) ===
          ' ' &&
        formattedCombinationNoTrailingWhitespace
          .toString()
          .charAt(formattedCombinationNoTrailingWhitespace.length - 2) !== ' '
      ) {
        formattedCombinationNoTrailingWhitespace =
          formattedCombinationNoTrailingWhitespace
            .toString()
            .substring(0, formattedCombinationNoTrailingWhitespace.length - 1);
      }

      const topCaptionText = topCaption ? topCaption.captionText : '';
      const bottomCaptionText = bottomCaption ? bottomCaption.captionText : '';

      const basketLine: BasketLineRequestModel = {
        combination: formattedCombinationNoTrailingWhitespace
          .toString()
          .split(' ')
          .join(''),
        combination_Layout: formattedCombinationNoTrailingWhitespace.toString(),
        transactionTypeId: props.transactionType.id,
        vehicleTypeId: props.vehicleType.id,
        plateDesignId: plates.plateDesignId,
        foregroundColourId: plates.textColorId,
        plateSizeId: plates.plateSizeId,
        dualPlateDesignId: plates.dualPlateDesignId,
        dualPlateForegroundColourId: plates.dualPlateTextColorId,
        dualPlateSizeId: plates.dualPlateSizeId,
        captionTopColourId: topCaption ? topCaption.captionColourId : undefined,
        captionTopText: topCaptionText,
        captionBottomColourId: bottomCaption
          ? bottomCaption.captionColourId
          : undefined,
        captionBottomText: bottomCaptionText,
        isGift: props.isGift,
      };
      const transactionTypeId =
        props.transactionType.id == TransactionTypesSetting.New.id
          ? OrderTypes.New
          : OrderTypes.Redesign;
      const transactionType =
        props.transactionType.id == TransactionTypesSetting.New.id
          ? 'New Design'
          : 'Redesign';

      mapCartItemToGAEcommerceEventModel(
        GA4Event.AddToCart,
        props.apiUrl,
        false,
        props.currentPlateDesign,
        props.price,
        props.transactionType,
      ).then((data) => {
        GAService.trackEvent('AddToCart', {
          combination: formattedCombinationNoTrailingWhitespace.toString(),
          plateDesignId: plates.plateDesignId,
          plateForegroundColourId: plates.textColorId,
          captionTopText: topCaptionText,
          captionBottomText: bottomCaptionText,
          orderType: transactionTypeId,
          itemID: data?.gaData.itemID,
          itemName: data?.gaData.itemName,
          variant: data?.gaData.variant,
          restyleVSnewdesign: transactionType,
        });

        GAService.trackEventGA4(data?.ga4Data);
      });

      if (
        isUserLoggedIn &&
        user.isDealer &&
        (basket?.basketLines?.length ?? 0) > 0
      ) {
        basket?.basketLines.map(async (basketLine) => {
          await deleteBasketLine(
            clientEnv.apiUrl,
            basket.basketId,
            basketLine.basketLineId,
          );
        });
      }

      let checkoutStates = undefined;
      if (buyAsGift) {        
        if (giftingType == undefined) {
          checkoutStates = {
            isGifting: true,
            manufactureNow: false,
            ownerIsApplicant: false,
            giftingType: 2,
          };
          // useCheckoutUIStore.setState({
          //   isGifting: true,
          //   manufactureNow: false,
          //   ownerIsApplicant: false,
          //   giftingType: 2,
          // });
        } else {
          checkoutStates = {
            isGifting: true,
            manufactureNow: false,
            ownerIsApplicant: false,
            giftingType: giftingType,
          };
          // useCheckoutUIStore.setState({
          //   isGifting: true,
          //   manufactureNow: false,
          //   ownerIsApplicant: false,
          //   giftingType: giftingType,
          // });
        }
        // await addBasketLine(
        //   clientEnv.apiUrl,
        //   basket.basketId,
        //   basketLine,
        //   // true,
        //   // true,
        //   // payload
        // );
      } else {
        checkoutStates = {
          isGifting: false,
          manufactureNow: false,
          ownerIsApplicant: ownerIsApplicant,
          giftingType: undefined,
        };
        // if (basket.basketLines.length > 0) {
        //   useCheckoutUIStore.setState({
        //     isGifting: false,
        //     manufactureNow: false,
        //     ownerIsApplicant: ownerIsApplicant,
        //     giftingType: 0,
        //   });
        // }
        // const addBasketLineResponse = await addBasketLine(
        //   clientEnv.apiUrl,
        //   basket.basketId,
        //   basketLine,
        //   // true,
        //   // false,
        //   // payload
        // );
        // if (addBasketLineResponse.success) {
        // }
        //TODO: if basketId not exist and it just created from above addBasketLine, the basket.BasketId may not available for the session
        //      service, if that's the case update addBasketLine to return basketId and basketLineId
      }
      
      updateMultiple(checkoutStates);
      
      addBasketLine(clientEnv.apiUrl, basket.basketId, basketLine).then(
        (res) => {
                    if (res.success && res.basketId) {
            SessionService.createSessionIfNot(res.basketId).then((ss) => {
              if (ss?.success) {
                if (user.isDealer) {
                  updateState('manufactureNow', true);
                  
                  if (typeof window !== 'undefined') {
                    navigate('/plate-manufacture-selection');
                  }
                } else {
                  if (checkoutStates.isGifting) {
                    if (typeof window !== 'undefined') {
                      navigate('/select-gifting-type');
                    }
                    // } else if (!actionFetcher.data.isCheckout) {
                    //   if (typeof window !== "undefined") {
                    //     window.location.href = `/add-to-cart/?combination=${basketLine?.combination.toUpperCase()}`;
                    //   }
                    //   navigate(`/add-to-cart/?combination=${basketLine?.combination.toUpperCase()}`);
                  } else {
                    if (typeof window !== 'undefined') {
                      navigate('/shopping-cart');
                    }
                  }
                }
              }
            });
          }
        },
      );
    } else {
      setState((pre) => ({
        ...pre, isBuyAsGiftClick: false,
        isAddToCartClick: false}))
    }
  };
  const triggerChooseDesignFloodlight = (textColorId?: number) => {
    const isDefault = isDefaultCombination(
      props.formattedCombination,
      props.vehicleType,
    );
    if (!isDefault) {
      const foregroundColorId =
        textColorId === undefined
          ? props.currentPlateDesign.frontPlate.textColor.id
          : textColorId;
      const topCaption = getTopCatpion(
        props.currentPlateDesign.frontPlate,
        props.currentPlateDesign.backPlate,
      );
      const bottomCaption = getBottomCatpion(
        props.currentPlateDesign.frontPlate,
        props.currentPlateDesign.backPlate,
      );

      const topCaptionText = topCaption ? topCaption.captionText : '';
      const bottomCaptionText = bottomCaption ? bottomCaption.captionText : '';
      const combination = props.formattedCombination;
      const plateDesignId = props.currentPlateDesign.frontPlate.plateDesignCode;
      const transactionType =
        props.transactionType.id == TransactionTypesSetting.New.id
          ? 'New Design'
          : 'Redesign';

      mapViewItemToGAEcommerceEventModel(
        GA4Event.ViewItem,
        props.apiUrl,
        false,
        props.transactionType.code,
        props.currentPlateDesign,
        props.metaInfo,
      ).then((data) => {
        GAService.trackEvent('chooseDesign', {
          isAvailable: true,
          combination: combination,
          plateDesignId: plateDesignId,
          plateForegroundColourId: foregroundColorId,
          captionTopText: topCaptionText,
          captionBottomText: bottomCaptionText,
          itemID: data?.gaData.itemID,
          itemName: data?.gaData.itemName,
          variant: data?.gaData.variant,
          restyleVSnewdesign: transactionType,
        });

        GAService.trackEventGA4(data?.ga4Data);
      });
    }
  };
  const handleContinue = () => {
    if (validateForm()) {
      //continue
      navigate('/create/', {
        state: {
          combination: props.formattedCombination,
          plateDesign: props.currentPlateDesign,
          vehicleType: props.vehicleType,
          transactionType: props.transactionType,
          categoryName: props.metaInfo.name,
        },
      });
    }
  };
  const validateForm = () => {
    const { frontPlate, backPlate } = props.currentPlateDesign;
    return (
      frontPlate &&
      validatePlateDesign(frontPlate) &&
      ((backPlate && validatePlateDesign(backPlate)) || backPlate === undefined)
    );
  };
  const validatePlateDesign = (plateDesign: PlateDesignModel) => {
    if (plateDesign.captionPositon !== undefined) {
      if (
        plateDesign.captionPositon.value ===
          MessagePlateLocations.TopAndBottom ||
        plateDesign.captionPositon.value === MessagePlateLocations.TopOnly
      ) {
        if (
          plateDesign.topMessageText === undefined ||
          plateDesign.topMessageText.trim().length === 0
        ) {
          setState((pre) => ({ ...pre, topMessageMissing: true }));
          return false;
        }
      }
      if (
        plateDesign.captionPositon.value ===
          MessagePlateLocations.TopAndBottom ||
        plateDesign.captionPositon.value === MessagePlateLocations.BottomOnly
      ) {
        if (
          plateDesign.bottomMessageText === undefined ||
          plateDesign.bottomMessageText.trim().length === 0
        ) {
          setState((pre) => ({ ...pre, bottomMessageMissing: true }));
          return false;
        }
      }
    }
    return true;
  };
  const topMessageTextChanged = (
    event: SyntheticEvent<HTMLElement, Event>,
    data: { value: string },
  ) => {
    const frontPlate = props.currentPlateDesign.frontPlate;
    const backPlate = props.currentPlateDesign.backPlate;
    const currentCaption = getCurrentCaptionPosition(
      frontPlate.captionPositon,
      backPlate.captionPositon,
    );
    let input = data.value;
    const captionMaxLength = frontPlate.captionMaxLength
      ? frontPlate.captionMaxLength
      : backPlate.captionMaxLength
        ? backPlate.captionMaxLength
        : undefined;
    input = PlateService.trimCaptionMessage(
      props.currentPlateDesign.frontPlate.plateDesignCode,
      props.fontMap,
      currentCaption?.value,
      input.toUpperCase(),
      captionMaxLength,
    );
    setState((pre) => ({ ...pre, topMessageMissing: false }));
    props.handleTopMessageTextChange(event, { value: input });
  };

  const bottomMessageTextChanged = (
    event: SyntheticEvent<HTMLElement, Event>,
    data: { value: string },
  ) => {
    const frontPlate = props.currentPlateDesign.frontPlate;
    const backPlate = props.currentPlateDesign.backPlate;

    const currentCaption = getCurrentCaptionPosition(
      frontPlate.captionPositon,
      backPlate.captionPositon,
    );
    let input = data.value;

    const captionMaxLength = frontPlate.captionMaxLength
      ? frontPlate.captionMaxLength
      : backPlate.captionMaxLength
        ? backPlate.captionMaxLength
        : undefined;
    input = PlateService.trimCaptionMessage(
      props.currentPlateDesign.frontPlate.plateDesignCode,
      props.fontMap,
      currentCaption?.value,
      input.toUpperCase(),
      frontPlate.bottomCaptionMaxLength == undefined ||
        frontPlate.bottomCaptionMaxLength == null
        ? captionMaxLength
        : frontPlate.bottomCaptionMaxLength,
    );
    setState((pre) => ({ ...pre, bottomMessageMissing: false }));
    props.handleBottomMessageTextChange(event, { value: input });
  };
  const onBrandChange = (
    event: SyntheticEvent<HTMLElement, Event>,
    data: DropdownProps,
  ) => {
    setState((pre) => ({
      ...pre,
      topMessageMissing: false,
      bottomMessageMissing: false,
    }));
    props.handleBrandChange(event, data, props.currentPlateDesign);
  };
  const onPlateColorChange = (data: string) => {
    setState((pre) => ({
      ...pre,
      topMessageMissing: false,
      bottomMessageMissing: false,
    }));
    props.handlePlateColorChange(props.currentPlateDesign, data);
  };
  const onFrontPlateSizeChange = (
    event: SyntheticEvent<HTMLElement, Event>,
    data: DropdownProps,
  ) => {
    setState((pre) => ({
      ...pre,
      topMessageMissing: false,
      bottomMessageMissing: false,
    }));
    props.handleFrontPlateSizeChange(event, data);
  };
  const onBackPlateSizeChange = (
    event: SyntheticEvent<HTMLElement, Event>,
    data: DropdownProps,
  ) => {
    setState((pre) => ({
      ...pre,
      topMessageMissing: false,
      bottomMessageMissing: false,
    }));
    props.handleBackPlateSizeChange(event, data);
  };
  const onPlateSizeChange = (
    event: SyntheticEvent<HTMLElement, Event>,
    data: DropdownProps,
  ) => {
    setState((pre) => ({
      ...pre,
      topMessageMissing: false,
      bottomMessageMissing: false,
    }));
    props.handleFrontPlateSizeChange(event, data);
    props.handleBackPlateSizeChange(event, data);
  };
  const onCaptionPositionChange = (
    event: SyntheticEvent<HTMLElement, Event>,
    data: DropdownProps,
  ) => {
    setState((pre) => ({
      ...pre,
      topMessageMissing: false,
      bottomMessageMissing: false,
    }));
    props.handleCaptionPositionChange(event, data);

    const frontPlate = props.currentPlateDesign.frontPlate;
    const backPlate = props.currentPlateDesign.backPlate;
    const captionMaxLength = frontPlate.captionMaxLength
      ? frontPlate.captionMaxLength
      : backPlate.captionMaxLength
        ? backPlate.captionMaxLength
        : undefined;

    const topCaption = PlateService.trimCaptionMessage(
      props.currentPlateDesign.frontPlate.plateDesignCode,
      props.fontMap,
      data.value?.toString(),
      props.currentPlateDesign.frontPlate.topMessageText?.toUpperCase(),
    );
    const bottomCaption = PlateService.trimCaptionMessage(
      props.currentPlateDesign.frontPlate.plateDesignCode,
      props.fontMap,
      data.value?.toString(),
      props.currentPlateDesign.frontPlate.bottomMessageText?.toUpperCase(),
      frontPlate.bottomCaptionMaxLength == undefined ||
        frontPlate.bottomCaptionMaxLength == null
        ? captionMaxLength
        : frontPlate.bottomCaptionMaxLength,
    );
    props.handleTopMessageTextChange(event, { value: topCaption });
    props.handleBottomMessageTextChange(event, { value: bottomCaption });
  };
  const onPlateTextColorChange = (data: TextColor) => {
    setState((pre) => ({
      ...pre,
      topMessageMissing: false,
      bottomMessageMissing: false,
    }));
    triggerChooseDesignFloodlight(data.id);
    props.handlePlateTextColorChange(props.currentPlateDesign, data);
  };
  const getTopMessageText = () => {
    const topMessage =
      props.currentPlateDesign.frontPlate.topMessageText === undefined
        ? props.currentPlateDesign.backPlate.topMessageText === undefined
          ? ''
          : props.currentPlateDesign.backPlate.topMessageText
        : props.currentPlateDesign.frontPlate.topMessageText;
    return topMessage;
  };
  const getBottomMessageText = () => {
    const bottomMessage =
      props.currentPlateDesign.frontPlate.bottomMessageText === undefined
        ? props.currentPlateDesign.backPlate.bottomMessageText === undefined
          ? ''
          : props.currentPlateDesign.backPlate.bottomMessageText
        : props.currentPlateDesign.frontPlate.bottomMessageText;
    return bottomMessage;
  };

  const getGiftBoxIcon = () => {
    let fillColor = '#000';
    if (state.isHover) {
      fillColor = '#000';
    } else {
      fillColor = '#3DAE2B';
    }
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="17"
        height="14"
        viewBox="0 0 17 15"
        fill="none"
      >
        <g clipPath="url(#clip0_330_9351)">
          <path
            d="M6.45312 2.15L7.54063 4H7.5H5.25C4.55937 4 4 3.44062 4 2.75C4 2.05938 4.55937 1.5 5.25 1.5H5.31875C5.78437 1.5 6.21875 1.74688 6.45312 2.15ZM2.5 2.75C2.5 3.2 2.60938 3.625 2.8 4H1.5C0.946875 4 0.5 4.44688 0.5 5V7C0.5 7.55312 0.946875 8 1.5 8H15.5C16.0531 8 16.5 7.55312 16.5 7V5C16.5 4.44688 16.0531 4 15.5 4H14.2C14.3906 3.625 14.5 3.2 14.5 2.75C14.5 1.23125 13.2688 0 11.75 0H11.6812C10.6844 0 9.75938 0.528125 9.25313 1.3875L8.5 2.67188L7.74687 1.39062C7.24062 0.528125 6.31562 0 5.31875 0H5.25C3.73125 0 2.5 1.23125 2.5 2.75ZM13 2.75C13 3.44062 12.4406 4 11.75 4H9.5H9.45938L10.5469 2.15C10.7844 1.74688 11.2156 1.5 11.6812 1.5H11.75C12.4406 1.5 13 2.05938 13 2.75ZM1.5 9V14.5C1.5 15.3281 2.17188 16 3 16H7.5V9H1.5ZM9.5 16H14C14.8281 16 15.5 15.3281 15.5 14.5V9H9.5V16Z"
            fill={fillColor}
          />
        </g>
        <defs>
          <clipPath id="clip0_330_9351">
            <rect
              width="16"
              height="17"
              fill="white"
              transform="translate(0.5)"
            />
          </clipPath>
        </defs>
      </svg>
    );
  };

  const {
    productCategoryList,
    currentPlateDesign,
    formattedCombination,
    vehicleType,
    metaInfo,
    price,
    loadingPrice,
  } = props;
  const isDefaultComb = isDefaultCombination(formattedCombination, vehicleType);
  const {
    handleTopMessageColorChange,
    handleBottomMessagecolorChange,
    handleLockedMessageColorChange,
  } = props;
  const frontPlate = props.currentPlateDesign.frontPlate;

  const backPlate = props.currentPlateDesign.backPlate;
  const captionMaxLength = frontPlate.captionMaxLength
    ? frontPlate.captionMaxLength
    : backPlate.captionMaxLength
      ? backPlate.captionMaxLength
      : undefined;
  const brands = productCategoryList.brands;
  const plateColors =
    brands !== undefined && currentPlateDesign.brand !== undefined
      ? productCategoryList.getBrandPlateColors(currentPlateDesign.brand)
      : productCategoryList.plateColors;
  const textColors = productCategoryList.getCurrentTextColors(
    currentPlateDesign.frontPlate.plateDesignCode,
    currentPlateDesign.backPlate.plateDesignCode,
  );
  const currentProductModel = productCategoryList.getCurrentProductModel(
    currentPlateDesign.frontPlate.plateDesignCode,
    currentPlateDesign.backPlate.plateDesignCode,
  );

  const plateSizes = currentProductModel.getProductSizes();
  const { captionPositions, messageColors } = productCategoryList
    .getPlateCategoryByColor(currentPlateDesign.plateColor.name)
    .getAllCaptionPositionsAndMessageColors();
  const currentCaptionPosition: CaptionPosition | undefined =
    getCurrentCaptionPosition(
      currentPlateDesign.frontPlate.captionPositon,
      currentPlateDesign.backPlate.captionPositon,
    );

  const showTopMessage =
    currentCaptionPosition !== undefined &&
    (currentCaptionPosition.value === MessagePlateLocations.TopAndBottom ||
      currentCaptionPosition.value === MessagePlateLocations.TopOnly);
  const showBottomMessage =
    currentCaptionPosition !== undefined &&
    (currentCaptionPosition.value === MessagePlateLocations.TopAndBottom ||
      currentCaptionPosition.value === MessagePlateLocations.BottomOnly);

  const topMessageColor =
    currentPlateDesign.frontPlate.topMessageColor !== undefined
      ? currentPlateDesign.frontPlate.topMessageColor
      : currentPlateDesign.backPlate.topMessageColor;
  const bottomMessageColor =
    currentPlateDesign.frontPlate.bottomMessageColor !== undefined
      ? currentPlateDesign.frontPlate.bottomMessageColor
      : currentPlateDesign.backPlate.bottomMessageColor;
  const topCaptionRemaining = PlateService.calculateCavityWidthRemaining(
    props.currentPlateDesign.frontPlate.plateDesignCode,
    props.fontMap,
    currentCaptionPosition,
    getTopMessageText(),
    captionMaxLength,
  );

  const bottomCaptionRemaining = PlateService.calculateCavityWidthRemaining(
    props.currentPlateDesign.frontPlate.plateDesignCode,
    props.fontMap,
    currentCaptionPosition,
    getBottomMessageText(),
    currentProductModel.bottomCaptionMaxLength == undefined ||
      currentProductModel.bottomCaptionMaxLength == null
      ? captionMaxLength
      : currentProductModel.bottomCaptionMaxLength,
  );

  let availableOnline = currentProductModel.dealerOnly === false;
  const authorizedDealers = currentProductModel.authorizedDealers;
  //const isDealerOrder = checkout?.isDealerOrder
  // const dealer = authorizedDealers?.dealers.filter(e => parseInt(e.dealerId) == checkout?.dealerDetails?.dealerId).find(() => true)
  const dealer = authorizedDealers?.dealers
    .filter((e) => parseInt(e.dealerId) == selectedDealerId)
    .find(() => true);
  if (dealer != undefined) {
    availableOnline = true;
  }

  const combination = props.formattedCombination.toString().split(' ').join('');

  const count = basket && basket.basketLines ? basket.basketLines.length : 0;
  const basketLine =
    count > 0
      ? basket.basketLines.find((c: any) => c.combination === combination)
      : undefined;
  const buttonTitle = basketLine === undefined ? 'ADD TO CART' : 'UPDATE';

  return (
    <DetailContainer isMobile={isMobile()} darkTheme={props.darkTheme}>
      <DetailHeading isMobile={isMobile()}>
        <H3 textAlign={isMobile() ? 'center' : 'left'}>{metaInfo.name}</H3>
        <PlateDesignPricing isMobile={isMobile()}>
          {isDefaultComb && <PricingFrom>From</PricingFrom>}
          {loadingPrice ? (
            <PriceLoadingMessage>Updating price ...</PriceLoadingMessage>
          ) : (
            <PricingAmount>${price}</PricingAmount>
          )}
        </PlateDesignPricing>
        {price !== undefined && !user.isDealer && (
          <CombinationPaymentList basePrice={price} isMobile={isMobile()} />
        )}
      </DetailHeading>
      <Description
        dangerouslySetInnerHTML={{ __html: metaInfo.description || '' }}
      />

      <ColorPaletteContainer>
        {brands !== undefined &&
          brands.length > 0 &&
          currentPlateDesign.brand !== undefined && (
            <PlateDropdownOptions
              darkTheme={props.darkTheme}
              title="Plate Brand"
              options={convertBrandsTopDropdownOptions(brands)}
              selectedValue={currentPlateDesign.brand.value}
              handleChange={onBrandChange}
            />
          )}
        {plateColors !== undefined && plateColors.length > 1 && (
          <PlateColorPalette
            colors={plateColors}
            card={false}
            selectedColor={currentPlateDesign.plateColor}
            title="Plate Colour"
            handleColorChange={onPlateColorChange}
            darkTheme={props.darkTheme}
          />
        )}
        {currentPlateDesign.brand === undefined
          ? textColors !== undefined &&
            textColors.length > 1 && (
              <TextColorPalette
                colors={textColors}
                selectedColor={currentPlateDesign.frontPlate.textColor}
                title="Text Colour"
                handleColorChange={onPlateTextColorChange}
                darkTheme={props.darkTheme}
              />
            )
          : textColors !== undefined &&
            textColors.length > 1 && (
              <TextColorPalette
                colors={textColors}
                selectedColor={currentPlateDesign.frontPlate.textColor}
                title="Text Colour"
                handleColorChange={onPlateTextColorChange}
                darkTheme={props.darkTheme}
              />
            )}
        {captionPositions.length > 1 &&
          currentCaptionPosition !== undefined && (
            <CaptionPositionWrapper>
              <PlateDropdownOptions
                darkTheme={props.darkTheme}
                title="Message Location"
                options={convertCaptionPositionsToDropdownOptions(
                  captionPositions,
                )}
                selectedValue={currentCaptionPosition.value}
                handleChange={onCaptionPositionChange}
              />
            </CaptionPositionWrapper>
          )}
        {showTopMessage &&
        showBottomMessage &&
        metaInfo.lockedMessageColor === true ? (
          <LockedMessagePlateSection
            titleTop="Top Plate Message"
            titleBottom="Bottom Plate Message"
            subtitleTop={topCaptionRemaining}
            subtitleBottom={bottomCaptionRemaining}
            colorTitle="Plate Message Color"
            plateMessageColors={messageColors}
            currentTextColor={topMessageColor}
            currentTextTop={
              currentPlateDesign.frontPlate.topMessageText === undefined
                ? ''
                : currentPlateDesign.frontPlate.topMessageText
            }
            currentTextBottom={
              currentPlateDesign.frontPlate.bottomMessageText === undefined
                ? ''
                : currentPlateDesign.frontPlate.bottomMessageText
            }
            handleColorChange={handleLockedMessageColorChange}
            handleMessageTopTextChange={topMessageTextChanged}
            handleMessageBottomTextChange={bottomMessageTextChanged}
            darkTheme={props.darkTheme}
          />
        ) : (
          <>
            {showTopMessage && (
              <MessagePlateSection
                title="Top Plate Message"
                subtitle={topCaptionRemaining}
                colorTitle="Top Plate Message"
                plateMessageColors={messageColors}
                currentTextColor={topMessageColor}
                currentText={
                  currentPlateDesign.frontPlate.topMessageText === undefined
                    ? ''
                    : currentPlateDesign.frontPlate.topMessageText
                }
                handleColorChange={handleTopMessageColorChange}
                handleMessageTextChange={topMessageTextChanged}
                darkTheme={props.darkTheme}
              />
            )}
            {showBottomMessage && (
              <MessagePlateSection
                title="Bottom Plate Message"
                subtitle={bottomCaptionRemaining}
                colorTitle="Bottom Plate Message"
                plateMessageColors={messageColors}
                currentTextColor={bottomMessageColor}
                currentText={
                  currentPlateDesign.frontPlate.bottomMessageText === undefined
                    ? ''
                    : currentPlateDesign.frontPlate.bottomMessageText
                }
                handleColorChange={handleBottomMessagecolorChange}
                handleMessageTextChange={bottomMessageTextChanged}
                darkTheme={props.darkTheme}
              />
            )}
          </>
        )}
      </ColorPaletteContainer>
      {plateSizes !== undefined && plateSizes.length > 1 && (
        <PlateDesignSizes>
          <Grid>
            {currentPlateDesign.frontPlate.disableDualSizing ||
              (currentPlateDesign.backPlate.disableDualSizing && (
                <PlateSizeDropdownContainer
                  mobile={16}
                  tablet={16}
                  computer={16}
                  largeScreen={8}
                  widescreen={8}
                >
                  <PlateDropdownOptions
                    darkTheme={props.darkTheme}
                    title="Select Plate Size"
                    subtitle="Size Guide"
                    subtitlePopup={<PlateSizeGuide />}
                    options={convertPlateSizeTopDropdownOptions(plateSizes)}
                    selectedValue={currentPlateDesign.frontPlate.plateSize.id}
                    handleChange={onPlateSizeChange}
                  />
                </PlateSizeDropdownContainer>
              ))}
            <Grid.Row>
              <PlateSizeDropdownContainer
                mobile={16}
                tablet={16}
                computer={16}
                largeScreen={8}
                widescreen={8}
              >
                <PlateDropdownOptions
                  darkTheme={props.darkTheme}
                  title="Select Front Plate Size"
                  subtitle="Size Guide"
                  subtitlePopup={<PlateSizeGuide />}
                  options={convertPlateSizeTopDropdownOptions(plateSizes)}
                  selectedValue={currentPlateDesign.frontPlate.plateSize.id}
                  handleChange={onFrontPlateSizeChange}
                />
              </PlateSizeDropdownContainer>
              <Grid.Column
                mobile={16}
                tablet={16}
                computer={16}
                largeScreen={8}
                widescreen={8}
              >
                <PlateDropdownOptions
                  darkTheme={props.darkTheme}
                  title="Select Back Plate Size"
                  subtitle="Size Guide"
                  subtitlePopup={<PlateSizeGuide />}
                  options={convertPlateSizeTopDropdownOptions(plateSizes)}
                  selectedValue={currentPlateDesign.backPlate.plateSize.id}
                  handleChange={onBackPlateSizeChange}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </PlateDesignSizes>
      )}
      <LegalDiv
        darkTheme={props.darkTheme}
        color={theme.brand.colors.darkGrey}
        top={14}
        bottom={0}
      >
        Plate images are only a guide. Colours and spacing may lety during
        manufacturing.
      </LegalDiv>
      {metaInfo.name.includes('Euro') && (
        <LegalDiv
          darkTheme={props.darkTheme}
          color={theme.brand.colors.darkGrey}
          top={10}
          bottom={20}
        >
          Please note: A slight letiation exists between the Navy Euro design
          colour and the Blue font colour.
        </LegalDiv>
      )}
      {!availableOnline && authorizedDealers && (
        <DealerOnlyProductMessage authorizedDealers={authorizedDealers} />
      )}
      {(state.topMessageMissing || state.bottomMessageMissing) && (
        <MessageError
          topMessageMissing={state.topMessageMissing}
          bottomMessageMissing={state.bottomMessageMissing}
        />
      )}
      {availableOnline &&
        (isDefaultComb ? (
          <div>
            <PlateHoleGuideLink url="/plate-hole-guide" openInNewTab={true}>
              View Plate hole guide
            </PlateHoleGuideLink>
            <span id="pdd-next">
              <KpButton
                id="pdd-next"
                fullWidth="mobile"
                buttonType="primary"
                color={theme.brand.colors.blue}
                onClick={handleContinue}
              >
                CONTINUE
              </KpButton>
            </span>
          </div>
        ) : (
          <div>
            <PlateHoleGuideLink url="/plate-hole-guide" openInNewTab={true}>
              View Plate hole guide
            </PlateHoleGuideLink>
            <span id="pdd-atc">
              <KpButton
                id="pdd-atc"
                fullWidth="mobile"
                buttonType="primary"
                color={theme.brand.colors.blue}
                loading={isAddingToCart && state.isAddToCartClick}
                onClick={() => {
                  setState((pre) => ({ ...pre, isAddToCartClick: true }));
                  handleAddToCart(false);
                }}
                darkTheme={true}
              >
                {user.isDealer ? 'CONTINUE' : buttonTitle}
              </KpButton>
            </span>
            {(count == 0 || (count == 1 && basketLine != undefined)) &&
              !user.isDealer && (
                <span id="pdd-gift-box">
                  <KpButton
                    id="pdd-gift-box"
                    fullWidth="mobile"
                    buttonType="darkModeSecondary"
                    color={theme.brand.colors.blue}
                    loading={isAddingToCart && state.isBuyAsGiftClick}
                    onClick={() => {
                      setState((pre) => ({ ...pre, isBuyAsGiftClick: true }));
                      handleAddToCart(true);
                    }}
                    darkTheme={true}
                    onMouseEnter={() => {
                      setState((pre) => ({ ...pre, isHover: true }));
                    }}
                    onMouseLeave={() => {
                      setState((pre) => ({ ...pre, isHover: false }));
                    }}
                  >
                    <div className="flex justify-center gap-1">
                      {getGiftBoxIcon()} BUY AS A GIFT
                    </div>
                  </KpButton>
                </span>
              )}
          </div>
        ))}
    </DetailContainer>
  );
};

const MessageError = ({
  topMessageMissing,
  bottomMessageMissing,
}: {
  topMessageMissing: boolean;
  bottomMessageMissing: boolean;
}) => {
  return (
    <div>
      <ErrorHeading>ERROR!</ErrorHeading>
      {topMessageMissing && (
        <Caption color={theme.brand.colors.error} top={10} bottom={15}>
          your Top Plate Message Field is Empty.
        </Caption>
      )}
      {bottomMessageMissing && (
        <Caption color={theme.brand.colors.error} top={10} bottom={15}>
          your Bottom Plate Message Field is Empty.
        </Caption>
      )}
    </div>
  );
};
const DealerOnlyProductMessage = ({
  authorizedDealers,
}: {
  authorizedDealers: AuthorizedDealers;
}) => {
  return (
    <div>
      <ErrorHeading>Product Unavailable Online!</ErrorHeading>
      <Caption color={theme.brand.colors.darkGrey} top={10} bottom={15}>
        {authorizedDealers.linkText}
      </Caption>
      <Modal
        trigger={<DealerLink>View Dealers</DealerLink>}
        closeIcon={<ModalCloseIcon name="close" />}
      >
        <Modal.Header>
          <ModalHeading>{authorizedDealers.title}</ModalHeading>
        </Modal.Header>
        <Modal.Content>
          <Paragraph1 color={theme.brand.colors.darkGrey}>
            {authorizedDealers.description}
          </Paragraph1>
          <List>
            {authorizedDealers.dealers.map((dealer) => {
              const telPhone = 'tel:' + dealer.phone;
              return (
                <DealerListItem key={dealer.name}>
                  <DealerContact>
                    {dealer.name}
                    {dealer.contact !== '' && (
                      <span>Contact: {dealer.contact}</span>
                    )}
                  </DealerContact>
                  <DealerPhone>
                    <LinkText href={telPhone}>{dealer.formattedPhone}</LinkText>
                  </DealerPhone>
                </DealerListItem>
              );
            })}
          </List>
        </Modal.Content>
      </Modal>
    </div>
  );
};

export default PlateDesignDetails;
